/* Haroutine clinical study gallery. No dependencies; one custom-element instance per section. */
(() => {
  const name = 'haroutine-study-gallery';
  if (customElements.get(name)) return;
  class HaroutineStudyGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.panels = Array.from(this.querySelectorAll('.hc-gallery-panel'));
      if (!this.panels.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.thumbnails = Array.from(this.querySelectorAll('.hc-thumbnail'));
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.counter = this.querySelector('[data-counter]');
      this.live = this.querySelector('[data-live]');
      this.media = window.matchMedia('(max-width: 749px)');
      this.active = 0;
      this.pointer = null;
      this.classList.add('hc-enhanced');
      this.querySelectorAll('.hc-range-wrap').forEach(el => { el.hidden = false; });
      this.querySelector('.hc-gallery-tools').hidden = this.panels.length < 2;
      this.previous.addEventListener('click', () => this.select(this.active - 1), options);
      this.next.addEventListener('click', () => this.select(this.active + 1), options);
      this.thumbnails.forEach(button => button.addEventListener('click', () => {
        this.select(this.panels.findIndex(panel => panel.dataset.blockId === button.dataset.forBlock));
      }, options));
      this.addEventListener('input', event => {
        if (event.target.matches('.hc-range')) this.reveal(event.target.closest('.hc-gallery-panel'), event.target.value);
      }, options);
      this.addEventListener('dragstart', event => {
        if (event.target.closest('.hc-comparison')) event.preventDefault();
      }, options);
      this.addEventListener('pointerdown', event => this.startDrag(event), options);
      this.addEventListener('pointermove', event => {
        if (this.pointer && event.pointerId === this.pointer.id) this.revealAt(event);
      }, options);
      const stop = event => this.stopDrag(event);
      this.addEventListener('pointerup', stop, options);
      this.addEventListener('pointercancel', stop, options);
      this.addEventListener('lostpointercapture', stop, options);
      this.onViewportChange = () => this.pageThumbnails();
      if (this.media.addEventListener) this.media.addEventListener('change', this.onViewportChange);
      else this.media.addListener(this.onViewportChange);
      document.addEventListener('shopify:block:select', event => {
        const id = event.detail && event.detail.blockId;
        const sectionId = event.detail && event.detail.sectionId;
        if (sectionId && sectionId !== this.dataset.sectionId) return;
        const index = this.panels.findIndex(panel => panel.dataset.blockId === id);
        if (index >= 0) this.select(index);
      }, options);
      this.select(0, false);
    }
    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
      if (this.media.removeEventListener) this.media.removeEventListener('change', this.onViewportChange);
      else this.media.removeListener(this.onViewportChange);
      this.pointer = null;
    }
    select(index, announce = true) {
      if (index < 0 || index >= this.panels.length) return;
      this.active = index;
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
      const selected = this.panels[index];
      this.thumbnails.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.forBlock === selected.dataset.blockId)));
      this.previous.disabled = index === 0;
      this.next.disabled = index === this.panels.length - 1;
      this.counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.panels.length).padStart(2, '0')}`;
      this.pageThumbnails();
      this.reveal(selected, Number(this.dataset.start) || 0);
      if (announce) this.live.textContent = `${selected.dataset.pairTitle}. ${index + 1} / ${this.panels.length}.`;
    }
    pageThumbnails() {
      const requested = Number(this.media.matches ? this.dataset.mobileThumbs : this.dataset.desktopThumbs);
      const size = Math.max(1, Math.min(8, requested || 4));
      const start = Math.floor(this.active / size) * size;
      this.thumbnails.forEach((button, i) => { button.hidden = i < start || i >= start + size; });
    }
    reveal(panel, raw) {
      const value = Math.max(0, Math.min(100, Number(raw)));
      if (!panel || !Number.isFinite(value)) return;
      const stage = panel.querySelector('.hc-comparison');
      const input = panel.querySelector('.hc-range');
      stage.style.setProperty('--hc-reveal', `${value}%`);
      if (input) {
        input.value = String(Math.round(value));
        input.setAttribute('aria-valuetext', `${Math.round(value)}% ${this.dataset.beforeLabel}, ${100 - Math.round(value)}% ${this.dataset.afterLabel}`);
      }
    }
    startDrag(event) {
      const stage = event.target.closest('.hc-comparison');
      if (!stage || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      const panel = stage.closest('.hc-gallery-panel');
      if (panel.dataset.ready !== 'true' || panel.hidden) return;
      this.pointer = { id: event.pointerId, stage, panel };
      if (stage.setPointerCapture) stage.setPointerCapture(event.pointerId);
      this.revealAt(event);
    }
    revealAt(event) {
      const bounds = this.pointer.stage.getBoundingClientRect();
      if (bounds.width > 0) this.reveal(this.pointer.panel, (event.clientX - bounds.left) / bounds.width * 100);
    }
    stopDrag(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const stage = this.pointer.stage;
      this.pointer = null;
      if (stage.hasPointerCapture && stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
    }
  }
  customElements.define(name, HaroutineStudyGallery);
})();
